import { useEffect, useLayoutEffect, useState } from "react";

/**
 * Adapted from usehooks-ts `useMediaQuery`
 * @see https://usehooks-ts.com/react-hook/use-media-query
 * @see https://github.com/juliencrn/usehooks-ts/blob/master/packages/usehooks-ts/src/useMediaQuery/useMediaQuery.ts
 */

type UseMediaQueryOptions = {
  /** Value returned during SSR / when `window` is unavailable. @default false */
  defaultValue?: boolean;
  /**
   * If true, read the query on first render. Set false for SSR so the first
   * paint uses `defaultValue` instead. @default true
   */
  initializeWithValue?: boolean;
};

const IS_SERVER = typeof window === "undefined";

const useIsomorphicLayoutEffect = IS_SERVER ? useEffect : useLayoutEffect;

/**
 * Tracks whether a CSS media query currently matches.
 */
export function useMediaQuery(
  query: string,
  {
    defaultValue = false,
    initializeWithValue = true,
  }: UseMediaQueryOptions = {},
): boolean {
  const getMatches = (mediaQuery: string): boolean => {
    if (IS_SERVER) return defaultValue;
    return window.matchMedia(mediaQuery).matches;
  };

  const [matches, setMatches] = useState<boolean>(() => {
    if (initializeWithValue) return getMatches(query);
    return defaultValue;
  });

  function handleChange() {
    setMatches(getMatches(query));
  }

  useIsomorphicLayoutEffect(() => {
    const matchMedia = window.matchMedia(query);
    handleChange();

    // Prefer deprecated listeners for Safari < 14 (usehooks-ts #135).
    if (matchMedia.addListener) {
      matchMedia.addListener(handleChange);
    } else {
      matchMedia.addEventListener("change", handleChange);
    }

    return () => {
      if (matchMedia.removeListener) {
        matchMedia.removeListener(handleChange);
      } else {
        matchMedia.removeEventListener("change", handleChange);
      }
    };
  }, [query]);

  return matches;
}
